import React, { useEffect, forwardRef, ReactNode, useRef } from 'react';

declare let window: any;
let autoComplete: any;

const loadScript = (url: string, callback: () => void) => {
    if (document.getElementById('google-maps-script')) {
        callback();
        return;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.defer = true;
    script.id = 'google-maps-script';
    script.src = url;

    script.onload = () => callback();

    document.head.appendChild(script);
};

async function handleScriptLoad(
    autoCompleteRef: any,
    search: any,
    setEnableSearch: any
) {
    if (!window.google || !window.google.maps || !window.google.maps.places)
        return;

    const { AutocompleteSessionToken } = await window.google.maps.importLibrary("places")
    autoComplete = new window.google.maps.places.Autocomplete(
        autoCompleteRef?.current,
        {
            sessionToken: AutocompleteSessionToken,
            types: ['(cities)'],
            componentRestrictions: {
                country: ['us']
            },
        }
    );
    autoComplete.setFields(['address_components', 'formatted_address']);
    autoComplete.addListener('place_changed', () => {
        const oldQuery = getOldQuery();
        if (oldQuery === autoCompleteRef?.current?.value) return;
        search();
    });

    autoCompleteRef?.current?.addEventListener(
        'keydown',
        (event: KeyboardEvent) => {
            if (event?.key === 'Enter') {
                const pacSelectedItem =
                    document.querySelector('.pac-item-selected');
                if (pacSelectedItem) {
                    search();
                } else {
                    sendKeyDown();
                }
                event.stopPropagation();
                event.preventDefault();
            }
        }
    );
}

const sendKeyDown = () => {
    const searchInput = document.getElementById('location_search_input');
    if (!searchInput) return;
    searchInput.dispatchEvent(
        new KeyboardEvent('keydown', {
            key: 'ArrowDown',
            code: 'ArrowDown',
            which: 40,
            keyCode: 40,
        })
    );
};

const getOldQuery = () => {
    const params = (location.search || '').split('&');
    for (const element of params) {
        if (element.startsWith('?location') || element.startsWith('location')) {
            const paramValues = element.split('=');
            if (paramValues.length < 2) return '';
            return decodeURIComponent(paramValues[1].trim()).replaceAll(
                /\+/g,
                ' '
            );
        }
    }
    return '';
};

interface Props {
    children?: ReactNode;
    onSearch: any;
    setEnableSearch: any;
    hasMRA?: boolean;
    ref: any;
}

export type Ref = HTMLInputElement;

export const SearchLocationInput = forwardRef<Ref, Props>((props, ref) => {
    const isScriptLoaded = useRef(false);

    useEffect(() => {
        if (
            !window.google &&
            !document.getElementById('google-maps-script') &&
            !isScriptLoaded.current
        ) {
            isScriptLoaded.current = true;
            props.setEnableSearch(true);

            // Add Google Maps API script with &loading=async parameter
            loadScript(
                `https://maps.googleapis.com/maps/api/js?key=${process.env.NEXT_PUBLIC_GOOGLE_API_KEY}&libraries=places&loading=async`,
                () =>
                    handleScriptLoad(ref, props.onSearch, props.setEnableSearch)
            );
        } else if (window.google) {
            handleScriptLoad(ref, props.onSearch, props.setEnableSearch);
        }

    }, [props.onSearch, props.setEnableSearch, ref]);

    useEffect(() => {
        return () => {
            const refAuto: any = ref;
            if (refAuto?.current) {
                window.google?.maps?.event?.clearInstanceListeners(refAuto?.current);
            }
        }
    }, [])

    return (
        <div className="search-location-input w-full">
            <label
                htmlFor="city_state"
                aria-label="city state"
                className="hidden"
            >
                City state:
            </label>
            <input
                id="location_search_input"
                aria-label="City state"
                name="city_state"
                ref={ref}
                placeholder={props?.hasMRA ? "City, State" : "City, state, or zip code"}
                type="search"
            />
        </div>
    );
});
